import {
  EMPTY,
  formatMoney,
  formatPercent,
  type IndicatorTone,
  type ProjectIndicators,
} from '@pmai/contracts';
import { ToneBar } from '@pmai/ui';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../shared/auth/session';

type Key = 'time' | 'scope' | 'income' | 'expenses' | 'profit' | 'margin';

/** One indicator: the percentage in its header (also the bar), colour and the line below. */
interface Column {
  key: Key;
  pct: number;
  tone: IndicatorTone;
  text: string;
  over?: boolean;
}

/**
 * The indicator strip of a project (FR-PRJ-017…023) — project card and overview. Without
 * financial management, Expenses/Profit/Margin give way to a message; readers without
 * `finance.view` see Time and Scope only (ADR 0004).
 */
export function IndicatorStrip({
  indicators: i,
  financialManagement,
}: {
  indicators: ProjectIndicators;
  financialManagement: boolean;
}) {
  const { t } = useTranslation();
  const finance = usePermissions().includes('finance.view');
  const label = (key: string) => t(`indicators.${key}`);
  const columns: Record<Key, Column | null> = {
    time: i.time && {
      key: 'time',
      pct: Math.min(i.time.pct, 100),
      tone: i.time.tone,
      text: t('indicators.days', { passed: i.time.passed, total: i.time.total }),
    },
    scope: i.scope && {
      key: 'scope',
      pct: i.scope.pct,
      tone: 'green',
      text: t('indicators.done', { pct: i.scope.pct }),
    },
    income: i.income && {
      key: 'income',
      pct: i.income.pct,
      tone: i.income.tone,
      text: `${formatMoney(i.income.invoiced)} / ${formatMoney(i.income.revenue)}`,
    },
    expenses: i.expenses && {
      key: 'expenses',
      pct: i.expenses.pct,
      tone: i.expenses.tone,
      over: i.expenses.pct > 100,
      text: `${formatMoney(i.expenses.fact)} / ${formatMoney(i.expenses.plan)}`,
    },
    profit: i.profit && {
      key: 'profit',
      pct: i.profit.bar,
      tone: i.profit.tone,
      text: formatMoney(i.profit.amount),
    },
    margin: i.margin && {
      key: 'margin',
      pct: Math.min(Math.abs(i.margin.pct), 100),
      tone: i.margin.tone,
      text: formatPercent(i.margin.pct),
    },
  };
  const shown: Key[] = [
    'time',
    'scope',
    ...(finance ? (['income'] as const) : []),
    ...(finance && financialManagement ? (['expenses', 'profit', 'margin'] as const) : []),
  ];

  return (
    <section
      aria-label={label('label')}
      className="grid gap-4 rounded-card border border-border bg-surface p-4 shadow-sm sm:grid-cols-3 xl:grid-cols-6"
    >
      {shown.map((key) => {
        const c = columns[key];
        return (
          <div
            key={key}
            role="group"
            aria-label={label(key)}
            data-indicator={key}
            className="flex flex-col gap-1.5"
          >
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="font-medium text-ink-muted">{label(key)}</span>
              <span className="font-semibold tabular-nums">{c ? formatPercent(c.pct) : EMPTY}</span>
            </div>
            <ToneBar value={c?.pct ?? 0} tone={c?.tone ?? 'grey'} label={label(key)} over={c?.over} />
            <span className="text-xs text-ink-muted">{c ? c.text : label('noData')}</span>
          </div>
        );
      })}
      {finance && !financialManagement && (
        <p className="flex items-center rounded-lg bg-canvas px-3 text-sm text-ink-muted sm:col-span-3">
          {t('timesheets.financeOff')}
        </p>
      )}
    </section>
  );
}
